import React from 'react';
import { Save, Play, Square, Download, PlusSquare, ArrowLeft } from 'lucide-react';
import { C } from '../../constants/design-tokens';
import { TextBtn } from '../../shared/ui/text-btn';
import { ResolutionConfig } from '../../shared/types/binding-types';
import { ActionButton } from '../../../components/buttons';
import { Spinner } from '../../../components/spinner';

interface EditorTopBarProps {
  onSave: () => void;
  onNew?: () => void;
  onBack?: () => void;
  onPreview: () => void;
  onExport: () => void;
  isPlaying: boolean;
  isExporting: boolean;
  isSaving?: boolean;
  isMediaLoading?: boolean;
  showPreview?: boolean;
  resolutions?: ResolutionConfig[];
  activeResolution?: ResolutionConfig;
  onResolutionChange?: (id: string) => void;
}

export const EditorTopBar: React.FC<EditorTopBarProps> = ({
  onSave,
  onNew,
  onBack,
  onPreview,
  onExport,
  isPlaying = false,
  isExporting = false,
  isSaving = false,
  isMediaLoading = false,
  showPreview = true,
  resolutions = [],
  activeResolution,
  onResolutionChange
}) => {
  const hasResolutions = resolutions.length > 1;
  // Con onBack o resolutions al menos ocupamos el slot izquierdo;
  const hasLeftSlot = Boolean(onBack) || hasResolutions;

  return (
    <div
      style={{
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: hasLeftSlot ? 'space-between' : 'flex-end',
        padding: '0 16px',
        background: C.bg,
        boxShadow: `2px 2px 5px ${C.border}`,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        flexShrink: 0,
        zIndex: 30
      }}
    >
      {hasLeftSlot && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onBack && (
            <TextBtn
              onClick={onBack}
              label='Back'
              icon={<ArrowLeft size={14} />}
              variant='ghost'
              disabled={isExporting}
            />
          )}
          {hasResolutions && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {resolutions.map(res => (
                <TextBtn
                  key={res.id}
                  onClick={() => onResolutionChange?.(res.id)}
                  label={res.label}
                  variant={res.id === activeResolution?.id ? 'secondary' : 'ghost'}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ActionButton variant='secondary' onClick={onSave} disabled={isExporting || isSaving}>
          {isSaving ? <Spinner size={14} /> : <Save size={14} />}
          Save
        </ActionButton>
        {/* <TextBtn onClick={onNew} label='Save as new' icon={<PlusSquare size={14} />} variant='ghost' /> */}
        {showPreview && (
          <TextBtn
            onClick={onPreview ?? (() => {})}
            label={isPlaying ? 'Stop' : 'Preview'}
            icon={isPlaying ? <Square size={14} /> : <Play size={14} />}
            variant='secondary'
            disabled={isExporting || isMediaLoading}
          />
        )}
        <ActionButton variant='primary' onClick={onExport} disabled={isSaving || isMediaLoading || isExporting}>
          {isExporting ? <Spinner size={14} /> : <Download size={14} />}
          Export
        </ActionButton>
      </div>
    </div>
  );
};
